// Landing page dinámica — renderiza desde DB la landing /lp/:slug
// El agente de marketing crea estas landings via create_landing_page tool.
// Track visit + conversion → marketing_funnels.

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { theme } from '../styles/theme';
import { thumbUrl } from '../lib/imageUrl';
import { getAttribution, getTrackingSessionId, trackEvent } from '../lib/tracking';

type Landing = {
  id: string;
  slug: string;
  title: string;
  hero_headline: string;
  hero_subhead: string | null;
  hero_asset_id: string | null;
  body_blocks: Array<{ type: string; content: any }>;
  cta_text: string;
  cta_whatsapp_phone: string;
  cta_prefilled_msg: string;
  social_proof: any;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  metadata: any;
};

type Asset = { url: string; thumbnail_url: string | null };

const TRACK_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-funnel`;

export function LandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const [lp, setLp] = useState<Landing | null>(null);
  const [heroAsset, setHeroAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data } = await supabase.from('landing_pages')
        .select('*').eq('slug', slug).eq('status', 'active').maybeSingle();
      if (data) {
        setLp(data as Landing);
        if (data.hero_asset_id) {
          const { data: a } = await supabase.from('marketing_assets')
            .select('url, thumbnail_url').eq('id', data.hero_asset_id).maybeSingle();
          if (a) setHeroAsset(a as Asset);
        }
        // SEO
        if (data.seo_title) document.title = data.seo_title;
        else document.title = `${data.title} · NULED`;
        // Track visit
        try {
          await fetch(TRACK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: 'visit',
              landing_page_slug: slug,
              utm_source: params.get('utm_source'),
              utm_medium: params.get('utm_medium'),
              utm_campaign: params.get('utm_campaign'),
              utm_content: params.get('utm_content'),
              utm_term: params.get('utm_term'),
              user_agent: navigator.userAgent,
            }),
          });
        } catch (_) { /* swallow */ }
      }
      setLoading(false);
    })();
  }, [slug]);

  const handleCtaClick = async () => {
    if (!lp) return;
    // Track conversion
    try {
      await fetch(TRACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'landing_conversion', landing_page_slug: lp.slug }),
      });
    } catch (_) {}
    // Build WhatsApp URL con UTM tags
    const utmTrail = [
      params.get('utm_source') && `utm_source=${params.get('utm_source')}`,
      params.get('utm_campaign') && `utm_campaign=${params.get('utm_campaign')}`,
      params.get('utm_content') && `utm_content=${params.get('utm_content')}`,
      `lp=${lp.slug}`,
    ].filter(Boolean).join('&');
    const leadRef = getTrackingSessionId();
    void trackEvent('click_whatsapp', {
      source: 'landing_page',
      landing_page_slug: lp.slug,
    });
    const msg = encodeURIComponent(`${lp.cta_prefilled_msg}\n\n_${utmTrail}_\n_Referencia web: ${leadRef}_`);
    window.location.href = `https://wa.me/${lp.cta_whatsapp_phone}?text=${msg}`;
  };

  // Conserva GCLID/UTMs aun cuando la landing use su embudo histórico.
  useEffect(() => { getAttribution(); }, [params]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.bgSoft, fontFamily: theme.font.sans, color: theme.textMuted }}>
      Cargando…
    </div>
  );
  if (!lp) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.bgSoft, fontFamily: theme.font.sans, color: theme.textMuted, flexDirection: 'column', gap: 12 }}>
      <h1 style={{ fontFamily: theme.font.head, color: theme.brand }}>Página no encontrada</h1>
      <a href="/" style={{ color: theme.accentDark, fontWeight: 700 }}>Ir a nuled.mx</a>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font.sans, color: theme.text }}>
      {/* HERO */}
      <section style={{
        background: theme.brand, color: '#fff', padding: '64px 24px 80px',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.7, textTransform: 'uppercase', marginBottom: 16, fontWeight: 700 }}>
            NULED · Mayorista Lutron
          </div>
          <h1 style={{
            fontFamily: theme.font.head, fontSize: 48, fontWeight: 700,
            margin: '0 0 16px', lineHeight: 1.1, color: '#fff',
          }}>
            {lp.hero_headline}
          </h1>
          {lp.hero_subhead && (
            <p style={{ fontSize: 18, lineHeight: 1.5, opacity: 0.9, maxWidth: 720, margin: '0 auto 32px' }}>
              {lp.hero_subhead}
            </p>
          )}
          <button onClick={handleCtaClick} style={{
            background: theme.accent, color: theme.brand, border: 'none',
            padding: '16px 32px', fontSize: 15, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer',
            fontFamily: theme.font.head, borderRadius: 2,
          }}>
            {lp.cta_text}
          </button>

          {heroAsset?.url && (
            <div style={{ marginTop: 48 }}>
              <img
                src={thumbUrl(heroAsset.url, 1000)}
                alt={lp.title}
                style={{ maxWidth: '100%', height: 'auto', borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Accent strip */}
      <div style={{ height: 4, background: theme.accent }} />

      {/* BODY BLOCKS dinámicos */}
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '64px 24px' }}>
        {(lp.body_blocks ?? []).map((block, i) => {
          if (block.type === 'text') {
            return (
              <div key={i} style={{ fontSize: 17, lineHeight: 1.6, marginBottom: 24, color: theme.text }}>
                {block.content}
              </div>
            );
          }
          if (block.type === 'heading') {
            return (
              <h2 key={i} style={{
                fontFamily: theme.font.head, fontSize: 28, fontWeight: 700,
                color: theme.brand, margin: '32px 0 16px',
              }}>
                {block.content}
              </h2>
            );
          }
          if (block.type === 'bullets') {
            return (
              <ul key={i} style={{ paddingLeft: 24, marginBottom: 24 }}>
                {(block.content as string[]).map((b, j) => (
                  <li key={j} style={{ fontSize: 16, lineHeight: 1.7, color: theme.text, marginBottom: 6 }}>{b}</li>
                ))}
              </ul>
            );
          }
          if (block.type === 'quote') {
            return (
              <blockquote key={i} style={{
                margin: '32px 0', padding: '20px 24px',
                background: theme.bgFooter, borderLeft: `4px solid ${theme.accent}`,
                fontSize: 17, fontStyle: 'italic', color: theme.text,
              }}>
                {block.content?.text}
                {block.content?.author && (
                  <footer style={{ marginTop: 12, fontSize: 13, color: theme.textMuted, fontStyle: 'normal', fontWeight: 700 }}>
                    — {block.content.author}
                  </footer>
                )}
              </blockquote>
            );
          }
          return null;
        })}
      </section>

      {/* SOCIAL PROOF si existe */}
      {lp.social_proof && (
        <section style={{ background: theme.bgFooter, padding: '48px 24px', borderTop: `1px solid ${theme.border}` }}>
          <div style={{ maxWidth: 920, margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 24, fontWeight: 700 }}>
              Confían en NULED
            </div>
            {lp.social_proof.logos && (
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                {(lp.social_proof.logos as string[]).map((logo, i) => (
                  <div key={i} style={{ fontSize: 14, fontWeight: 700, color: theme.brand, opacity: 0.6 }}>{logo}</div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section style={{ padding: '64px 24px', textAlign: 'center' }}>
        <button onClick={handleCtaClick} style={{
          background: theme.brand, color: '#fff', border: 'none',
          padding: '18px 36px', fontSize: 16, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer',
          fontFamily: theme.font.head, borderRadius: 2,
        }}>
          {lp.cta_text} →
        </button>
        <div style={{ marginTop: 16, fontSize: 13, color: theme.textMuted }}>
          Respuesta en menos de 30 minutos · Lun-Vie 9:00-18:00 CDMX
        </div>
      </section>

      {/* Footer mínimo */}
      <footer style={{ background: theme.brand, color: 'rgba(255,255,255,0.7)', padding: '24px', textAlign: 'center', fontSize: 11 }}>
        © NULED · Mayorista educador Lutron en México · <a href="/" style={{ color: theme.accent }}>nuled.mx</a>
      </footer>
    </div>
  );
}
